import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { apiUrl, mediaUrl } from '@/api/config';
import { getAuthToken, getUserData } from '@/utils/auth';
import { updateSystemBarsTheme } from '@/utils/systemBars';

const TeacherContext = createContext();

export const useTeacher = () => {
    const context = useContext(TeacherContext);
    if (!context) {
        throw new Error('useTeacher must be used within a TeacherProvider');
    }
    return context;
};

const CLASS_STORAGE_KEY = 'teacher_current_class';

export const TeacherProvider = ({ children }) => {
    const location = useLocation();
    // Theme State
    const [teacherTheme, setTeacherTheme] = useState(() => {
        try {
            return localStorage.getItem('teacherTheme') || 'light';
        } catch {
            return 'light';
        }
    });

    const isDark = teacherTheme === 'dark';

    const toggleTheme = () => {
        setTeacherTheme((prev) => {
            const next = prev === 'dark' ? 'light' : 'dark';
            try {
                localStorage.setItem('teacherTheme', next);
            } catch (e) {
                console.error('Failed to save teacherTheme', e);
            }
            return next;
        });
    };

    // Apply teacher theme to document root only while the teacher portal is active.
    // The student portal owns the global `.dark` class through PreferencesContext.
    useEffect(() => {
        const root = document.documentElement;
        const currentPath = `${location.pathname || ''}${location.search || ''}${location.hash || ''}${window.location.hash || ''}`;
        const isTeacherPortal = currentPath.includes('/teacher');

        if (!isTeacherPortal) {
            root.classList.remove('teacher-theme', 'teacher-theme-dark');
            return;
        }

        if (teacherTheme === 'dark') {
            root.classList.add('teacher-theme-dark', 'dark');
            root.classList.remove('teacher-theme');
            updateSystemBarsTheme(true, '#171f22');
        } else {
            root.classList.add('teacher-theme');
            root.classList.remove('teacher-theme-dark', 'dark');
            updateSystemBarsTheme(false, '#f6f3ef');
        }
    }, [location.hash, location.pathname, location.search, teacherTheme]);

    // Class State
    const [classes, setClasses] = useState([]);
    const [isLoadingClasses, setIsLoadingClasses] = useState(true);
    const [currentClass, setCurrentClassState] = useState(() => {
        try {
            const stored = localStorage.getItem(CLASS_STORAGE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch (e) {
            console.error('Failed to parse stored class', e);
            return null;
        }
    }); // Default to null (shows all classes overview)

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const token = getAuthToken('teacher');
                if (!token) {
                    // Skip loading in non-teacher sessions to avoid noisy console logs
                    setClasses([]);
                    setIsLoadingClasses(false);
                    return;
                }
                const res = await axios.get(apiUrl('v1/teachers/classes/'), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const apiClasses = res?.data?.classes || [];

                if (apiClasses.length === 0) {
                    console.warn('No classes returned from API. Response:', res.data);
                }

                const mapped = apiClasses.map((c, idx) => ({
                    id: c.id,
                    name: c.grade_name || `Class ${idx + 1}`,
                    subject: c.subject_name,
                    fullName: c.full_name || `${c.subject_name} - ${c.grade_name || ''}`,
                    students: c.student_count || 0,
                    averageScore: 0,
                    completionRate: 0,
                    recentProgress: '0%',
                    engagement: 0,
                    performance: {}
                }));
                setClasses(mapped);
                // If stored class no longer exists, reset
                if (currentClass && !mapped.find((c) => c.id === currentClass.id)) {
                    setCurrentClassState(null);
                }
            } catch (err) {
                console.error('Failed to load classes', err);
                if (err.response) {
                    console.error('API Error Response:', err.response.data);
                    console.error('API Error Status:', err.response.status);
                }
                setClasses([]);
            } finally {
                setIsLoadingClasses(false);
            }
        };
        fetchClasses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        try {
            if (currentClass) {
                localStorage.setItem(CLASS_STORAGE_KEY, JSON.stringify(currentClass));
            } else {
                localStorage.removeItem(CLASS_STORAGE_KEY);
            }
        } catch (e) {
            console.error('Failed to persist class selection', e);
        }
    }, [currentClass]);

    const setCurrentClass = (cls) => {
        setCurrentClassState(cls);
    };

    // User State (Dynamic from localStorage)
    const [user, setUser] = useState(() => {
        const userData = getUserData('teacher');
        if (userData) {
            const photoUrl = mediaUrl(userData.profile_photo || userData.profile_pic_url);
            return {
                name: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username || 'Teacher',
                role: 'Teacher',
                avatar: photoUrl,
                notifications: 2
            };
        }
        // Fallback if no user data
        return {
            name: 'Teacher',
            role: 'Teacher',
            avatar: null,
            notifications: 2
        };
    });

    // Update user state when profile is updated
    useEffect(() => {
        const handleProfileUpdate = () => {
            const userData = getUserData('teacher');
            if (userData) {
                const photoUrl = mediaUrl(userData.profile_photo || userData.profile_pic_url);
                setUser({
                    name: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username || 'Teacher',
                    role: 'Teacher',
                    avatar: photoUrl,
                    notifications: 2
                });
            }
        };

        const handleStorageChange = (e) => {
            if (e.key === 'teacher_user' || e.key === 'teacher_token') {
                handleProfileUpdate();
            }
        };

        window.addEventListener('profileUpdated', handleProfileUpdate);
        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('profileUpdated', handleProfileUpdate);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Helper function to get students from current class
    const getCurrentClassStudents = () => {
        if (!currentClass) return [];
        return currentClass.students || [];
    };

    // Helper function to get class label
    const getClassLabel = (classData) => {
        if (!classData) return 'All Classes';
        return `${classData.subject} - ${classData.name}`;
    };

    const value = {
        teacherTheme,
        isDark,
        toggleTheme,
        setTeacherTheme,
        classes,
        isLoadingClasses,
        currentClass,
        setCurrentClass,
        getCurrentClassStudents,
        getClassLabel,
        user
    };

    return (
        <TeacherContext.Provider value={value}>
            {children}
        </TeacherContext.Provider>
    );
};
